import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import {
  replaceHardwareProjectTrack,
  saveUpdateDescriptionTask,
} from "../../../../store/slices/managerEditTrackingEntries";
import { fetchAllProjects } from "../../../../store/slices/projectsSlice";
import useToolGuideSync from "../../../../features/toolGuide/useToolGuideSync";
import { MANAGER_BUCKET_KEYS } from "../../../../features/toolGuide/toolGuideConfig";
import {
  deleteDescriptionTask,
  fetchDescriptionTasksProjectTracking,
} from "../../../../store/slices/DescriptionTask";
import { fetchHardwareProjectTracking } from "../../../../store/slices/hardwareDescriptionSlice";
import HardwareEditAddOmAssigned from "./HardwareEditAddOmAssigned";
import { setProjectReadTrue } from "../../../../store/slices/readSlice";
import { useCurrencySymbol } from "../../../../utils/currencyUtils";
import {
  formatAmountComma,
  formatAmountForInput,
  validateAmountValue,
  parseAmountNumber,
} from "../../../../utils/decimalAmountUtils";
import { updateDescriptionData, updateHardwareDescriptionData } from "../../../../store/slices/HardwareSlice/updateHardwareDataSlice";
import { triggerEmployeesAssignedRefresh, triggerEmployeesAssignedStateRefresh } from "../../../../store/slices/ProjectAssign/projectAssignStateSlice";
import {
  consolidateTrainingExpenseRowsForDisplay,
  isInstallationAssignmentRow,
  isInstallationOtherExpenseTemplateDescription,
  isTrainingExpenseEntry,
  normalizeAssignmentTypeServiceKey,
  resolveTrainingAssigneeId,
  taskTypeServiceMatchesAssignmentRow,
  trainingMatchesEmployeeProject,
  unwrapTrainings,
} from "../../../../utils/trainingOtherExpenseSync";
import { normalizeOtherExpenseDescriptionKey } from "../../../../utils/projectAssignDescriptionMerge";

const ShowAssigedTableManagerHardware = ({
  refreshAssign,
  row,
  emp,
  setIsLoading,
  matchedOldRowDesctiption,
  showListState,
  clickedPersonId,
  setClickedPersonId,
  allAssignedForProject = [],
  projectDescriptionEntries = [],
  isViewOnly = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { syncToolGuideStep } = useToolGuideSync(
    MANAGER_BUCKET_KEYS.PROJECT_ASSIGNED,
    "L4_PM"
  );
  const currency = useCurrencySymbol();
  const employeesAssignedRefresh = useSelector(
    (state) => state.assignSlice.employeesAssignedRefresh
  );
  const { mytrainings } = useSelector((state) => state.myTrainings);
  const employeesCancelAssignedRefresh = useSelector(
    (state) => state.projectAssignCancelState.employeesCancelAssignedRefresh
  );
  const [projectDescriptionsAssiged, setProjectDescriptionsAssiged] = useState(
    []
  );
  const [tripDurationInput, setTripDuration] = useState();

  const handleTripDuration = (e) => {
    const value = e.target.value;
    setTripDuration(value);
  };

  const [hardwarePartsOp, setHardwarePartsOp] = useState([]);
  console.log("employeesCancelAssignedRefresh hardwarePartsOp", hardwarePartsOp)
  const tripDuration =
    tripDurationInput ?? projectDescriptionsAssiged?.[0]?.tripduration ?? "";

  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;
    if (emp?.HardwareParts?.length > 0) {
      setHardwarePartsOp(emp?.HardwareParts);
    } else {
      setHardwarePartsOp([
        {
          assemblepartdescription: "",
          partno: "",
          quantity: "",
          unitprice: "",
          totalprice: "",
        },
      ]);
    }
    isInitialized.current = true;
  }, [emp, setHardwarePartsOp, employeesAssignedRefresh, employeesCancelAssignedRefresh]);

  // For "Self" / hardware manager: editable rows use manager-owned entries; consolidated totals are computed for display
  const isSelfManager =
    emp?.fullName === "Self" ||
    (emp?.customRole?.[0] === "project manager" && emp?.customRole?.length === 1);
  const normDescKey = useCallback(
    (d) => normalizeOtherExpenseDescriptionKey(d),
    []
  );

  /** Exclude Installation-line description tasks on Hardware/Software/Cloud assign rows (shared lead projectid). */
  const descriptionEntryBelongsToAssignRow = useCallback(
    (entry) => {
      if (!entry || !taskTypeServiceMatchesAssignmentRow(entry, row)) return false;
      if (isInstallationAssignmentRow(row)) return true;
      const taskKey = normalizeAssignmentTypeServiceKey(
        entry?.typeservice ?? entry?.TypeService ?? entry?.typeService
      );
      if (taskKey === "installation") return false;
      const desc = String(entry?.description ?? "").trim();
      if (
        !taskKey &&
        isInstallationOtherExpenseTemplateDescription(desc) &&
        /\([^)]+\)\s*$/.test(desc)
      ) {
        return false;
      }
      return true;
    },
    [row]
  );

  const managerOnlyDescriptionList = useMemo(() => {
    let list = emp?.DescriptionList;
    if (isSelfManager && Array.isArray(emp?.DescriptionList)) {
      list = emp.DescriptionList.filter((d) => {
        if (d?.taskownerid == null || d?.taskownerid === "")
          return true;
        return String(d.taskownerid) === String(emp._id);
      });
    }
    return (list || []).filter(descriptionEntryBelongsToAssignRow);
  }, [isSelfManager, emp?.DescriptionList, emp?._id, descriptionEntryBelongsToAssignRow]);

  const descriptionListSyncKey = useMemo(
    () => JSON.stringify(managerOnlyDescriptionList ?? []),
    [managerOnlyDescriptionList]
  );

  const assignScopedDescriptionEntries = useMemo(
    () => (projectDescriptionEntries || []).filter(descriptionEntryBelongsToAssignRow),
    [projectDescriptionEntries, descriptionEntryBelongsToAssignRow]
  );

  const projectDescriptionEntriesSyncKey = useMemo(
    () => JSON.stringify(assignScopedDescriptionEntries ?? []),
    [assignScopedDescriptionEntries]
  );
  const isHardwareTrainingCostDescription = useCallback(
    (entry) =>
      isTrainingExpenseEntry(entry) ||
      normDescKey(entry?.description).startsWith("training cost"),
    [normDescKey]
  );

  const lockedTrainingRow = useMemo(() => {
    const assigneeId = String(emp?._id || "").trim();
    const assigneeEmail = String(emp?.EmpEmail || emp?.email || "").trim().toLowerCase();
    if (!assigneeId && !assigneeEmail) return null;

    const leadLike = row?.lead || row?.Lead || {};
    const projectMeta = {
      assigneeEmail,
      rowId: row?._id,
      projectIds: [
        row?._id,
        row?.id,
        row?.projectid,
        row?.projectId,
        row?.ProjectID,
        leadLike?._id,
        leadLike?.id,
      ].filter(Boolean),
      quotationIds: [
        row?.quotationId,
        row?.quotationID,
        row?.quotationid,
        row?.QuotationId,
        row?.QuotationID,
        leadLike?.quotationId,
        leadLike?.quotationID,
        leadLike?.quotationid,
        leadLike?.QuotationId,
        leadLike?.QuotationID,
      ].filter(Boolean),
      projectNames: [
        row?.ProjectName,
        row?.projectname,
        leadLike?.ProjectName,
        leadLike?.projectname,
      ].filter(Boolean),
      leadIds: [
        row?.leadid,
        row?.leadId,
        row?.LeadId,
        row?.LeadID,
        leadLike?.leadid,
        leadLike?.leadId,
        leadLike?.LeadId,
        leadLike?.LeadID,
      ].filter(Boolean),
    };

    const matched = unwrapTrainings(mytrainings).filter((training) => {
      if (assigneeId && trainingMatchesEmployeeProject(training, assigneeId, projectMeta)) {
        return true;
      }

      const assignedRaw = training?.assignedto ?? training?.assignedTo;
      const assignedId = resolveTrainingAssigneeId(assignedRaw);
      const assignedEmail =
        typeof assignedRaw === "string" && assignedRaw.includes("@")
          ? assignedRaw.trim().toLowerCase()
          : String(training?.assignedToEmail || "").trim().toLowerCase();
      const assigneeMatches =
        (assigneeId && assignedId && String(assignedId) === String(assigneeId)) ||
        (assigneeEmail && assignedEmail && assignedEmail === assigneeEmail);
      if (!assigneeMatches) return false;

      const projectNameKey = normDescKey(training?.projectname);
      const rowProjectNames = projectMeta.projectNames.map(normDescKey);
      return Boolean(projectNameKey && rowProjectNames.includes(projectNameKey));
    });

    const normalized = matched
      .map((training) => {
        const amount = Number(
          String(training?.trainingcost ?? training?.trainingCost ?? 0).replace(/,/g, "")
        );
        const notes = training?.notes ?? training?.note ?? "";
        return {
          amount: Number.isFinite(amount) ? amount : 0,
          notes: String(notes || "").trim(),
        };
      })
      .filter((item) => item.amount > 0 || item.notes);

    const persistedTrainingRows = (assignScopedDescriptionEntries || []).filter((entry) => {
      const ownerMatches = String(entry?.taskownerid ?? "") === String(assigneeId);
      return ownerMatches && isHardwareTrainingCostDescription(entry);
    });

    const currentTrainingTotal = normalized.reduce((sum, item) => sum + item.amount, 0);
    const persistedTrainingTotal = persistedTrainingRows.reduce(
      (sum, item) => sum + (Number(item?.value) || 0),
      0
    );
    const total = currentTrainingTotal > 0 ? currentTrainingTotal : persistedTrainingTotal;
    const joinedNotes = Array.from(
      new Set(
        [
          ...normalized.map((item) => item.notes),
          ...persistedTrainingRows.map((item) => item?.notes),
        ]
          .map((note) => String(note || "").trim())
          .filter(Boolean)
      )
    ).join("; ");

    if (total <= 0 && !joinedNotes) return null;
    return {
      description: `Training Cost (${currency})`,
      amount: total,
      notes: joinedNotes,
    };
  }, [
    currency,
    emp?._id,
    emp?.EmpEmail,
    emp?.email,
    isHardwareTrainingCostDescription,
    mytrainings,
    normDescKey,
    assignScopedDescriptionEntries,
    row,
  ]);

  const hasTrainingRowInAssignedDescriptions = useMemo(
    () =>
      (projectDescriptionsAssiged || []).some((entry) =>
        isHardwareTrainingCostDescription(entry)
      ),
    [isHardwareTrainingCostDescription, projectDescriptionsAssiged]
  );
  const lockedTrainingCostTotal = lockedTrainingRow
    ? Number(lockedTrainingRow.amount) || 0
    : 0;
  const shouldShowLockedTrainingRow =
    !hasTrainingRowInAssignedDescriptions &&
    (lockedTrainingCostTotal > 0 || String(lockedTrainingRow?.notes || "").trim() !== "");

  // Sum of amounts for a description from all OTHER persons on this project (from raw project description entries; display only, not saved)
  const getOperationSumForDescription = (description) => {
    const nd = normDescKey(description);
    if (!nd) return 0;
    const currentEmpId = String(emp?._id);
    return (assignScopedDescriptionEntries || []).reduce((sum, entry) => {
      if (isHardwareTrainingCostDescription(entry)) return sum;
      const ownerId = entry?.taskownerid;
      const isOtherPerson = ownerId != null && ownerId !== "" && String(ownerId) !== currentEmpId;
      if (isOtherPerson && normDescKey(entry?.description) === nd) {
        return sum + (Number(entry?.value) || 0);
      }
      return sum;
    }, 0);
  };

  const isDescriptionCommon = (description) => getOperationSumForDescription(description) > 0;

  // For Self: operation-only rows = descriptions that exist in operation but not in manager (one row per unique description with sum of amount)
  const managerDescKeys = useMemo(
    () => new Set((managerOnlyDescriptionList || []).map((d) => normDescKey(d?.description))),
    [managerOnlyDescriptionList, normDescKey]
  );
  const operationOnlyDisplayRows = useMemo(() => {
    if (!isSelfManager || !Array.isArray(assignScopedDescriptionEntries)) return [];
    const currentEmpId = String(emp?._id);
    const byKey = {};
    assignScopedDescriptionEntries.forEach((entry) => {
      if (isHardwareTrainingCostDescription(entry)) return;
      const ownerId = entry?.taskownerid;
      const isOtherPerson = ownerId != null && ownerId !== "" && String(ownerId) !== currentEmpId;
      if (!isOtherPerson) return;
      const key = normDescKey(entry?.description);
      if (!key || managerDescKeys.has(key)) return;
      if (!byKey[key]) {
        byKey[key] = { description: entry?.description ?? "", value: 0, notes: entry?.notes ?? "", _isOperationOnlyDisplay: true };
      }
      byKey[key].value += Number(entry?.value) || 0;
    });
    return Object.values(byKey);
  }, [isSelfManager, assignScopedDescriptionEntries, managerDescKeys, emp?._id, normDescKey, isHardwareTrainingCostDescription]);

  const getManagerOnlyAmountFromProject = (description) => {
    const nd = normDescKey(description);
    if (!nd) return 0;
    return (assignScopedDescriptionEntries || []).reduce((sum, entry) => {
      if (isHardwareTrainingCostDescription(entry)) return sum;
      if (normDescKey(entry?.description) !== nd) return sum;
      if (String(entry?.taskownerid ?? "") !== String(emp?._id)) return sum;
      return sum + (Number(entry?.value) || 0);
    }, 0);
  };

  const getDisplayAmount = (descriptionRow) => {
    const rowVal = Number(descriptionRow?.value) || 0;
    if (!isSelfManager) return rowVal;
    if (!isDescriptionCommon(descriptionRow?.description)) return rowVal;
    const mgrOnly = getManagerOnlyAmountFromProject(descriptionRow?.description);
    const opSum = getOperationSumForDescription(descriptionRow.description);
    if (opSum <= 0) return rowVal;
    return mgrOnly + opSum;
  };

  const getManagerOnlyAmountForSave = (entry) => {
    if (!isSelfManager || !isDescriptionCommon(entry?.description)) {
      return parseAmountNumber(entry?.value);
    }
    const mgrOnly = getManagerOnlyAmountFromProject(entry?.description);
    return mgrOnly > 0 ? mgrOnly : parseAmountNumber(entry?.value);
  };

  const isCommonConsolidatedRow = (descriptionRow) =>
    isSelfManager && isDescriptionCommon(descriptionRow?.description);

  const isDescriptionInit = useRef(false);
  const descriptionOwnerKey = emp?._id ?? "";

  useEffect(() => {
    isDescriptionInit.current = false;
  }, [
    descriptionOwnerKey,
    employeesAssignedRefresh,
    descriptionListSyncKey,
    projectDescriptionEntriesSyncKey,
  ]);

  useEffect(() => {
    if (!isDescriptionInit.current) {
      if (managerOnlyDescriptionList?.length > 0) {
        setProjectDescriptionsAssiged(consolidateTrainingExpenseRowsForDisplay(managerOnlyDescriptionList));
      } else {
        setProjectDescriptionsAssiged(Array.isArray(managerOnlyDescriptionList) ? managerOnlyDescriptionList : []);
      }
      isDescriptionInit.current = true;
    }
  }, [descriptionOwnerKey, employeesAssignedRefresh, descriptionListSyncKey, managerOnlyDescriptionList]);

  const handleTripInputChange = (index, field, value) => {
    const nextValue =
      field === "value" ? validateAmountValue(value) : value;
    setProjectDescriptionsAssiged((prev) => {
      const newRows = [...prev]; // shallow copy of array
      const updatedRow = { ...newRows[index], [field]: nextValue }; // clone object and update field
      newRows[index] = updatedRow; // replace with updated row
      return newRows;
    });
  };

  const handleAddRow = () => {
    if (isViewOnly) return;
    if (projectDescriptionsAssiged?.length === 0) {
      const newRow = {
        description: "",
        value: "",
        notes: "",
        ...(isSelfManager && emp?._id ? { taskownerid: emp._id } : {}),
      };
      setProjectDescriptionsAssiged([newRow]);
    } else if (projectDescriptionsAssiged.length < 15) {
      setProjectDescriptionsAssiged([
        ...projectDescriptionsAssiged,
        {
          description: "",
          value: "",
          notes: "",
          ...(isSelfManager && emp?._id ? { taskownerid: emp._id } : {}),
        },
      ]);
    } else {
    }
  };

  const handleRemoveRow = (index) => {
    if (isViewOnly) return;
    const updatedRows = projectDescriptionsAssiged?.filter(
      (_, i) => i !== index
    );
    setProjectDescriptionsAssiged(updatedRows);
  };

  const validateDescriptionOrNotes = (value) => {
    return value.replace(/[^a-zA-Z0-9 .,?'-]/g, "");
  };

  const handleHardwarePartSubmit = async (emp) => {
    if (isViewOnly) return;
    syncToolGuideStep("submit-task");
    const hardwareItems = hardwarePartsOp;
    const projectId = emp?.projectid;
    const cleanedHardwareParts = hardwareItems
      .filter(
        (entry) =>
          entry?.assemblepartdescription !== null &&
          entry?.assemblepartdescription !== undefined &&
          entry?.assemblepartdescription !== "" &&
          entry?.quantity !== null &&
          entry?.quantity !== undefined &&
          entry?.quantity !== ""
      )
      .map((entry) => ({
        _id: entry?._id || null,
        assemblepartdescription: entry.assemblepartdescription,
        partno: entry.partno,
        permonth: entry.permonth,
        quantity: entry.quantity,
        unitprice: entry.unitprice,
        totalprice: entry.totalprice,
        oldtaskid: entry.oldtaskid || null,
        taskownerid: entry.taskownerid || null,
      }));
    console.log("cleanedHardwareParts", cleanedHardwareParts, hardwareItems);
    let payload; // <-- define it here
    if (emp?.customRole[0] === "project manager") {
      const managerParts = cleanedHardwareParts.filter((part) => {
        // case 1: taskownerid is missing → assign to manager
        if (
          part?.taskownerid === null ||
          part?.taskownerid === undefined ||
          part?.taskownerid === ""
        ) {
          return true;
        }
        // case 2: taskownerid exists and matches manager
        return String(part.taskownerid) === String(emp?._id);
      });
      payload = {
        projectid: projectId,
        tasktype: "self",
        tasks: managerParts,
      };
      console.log("cleanedHardwareParts1 cleanedHardwareParts", cleanedHardwareParts);
      console.log("cleanedHardwareParts1 managerParts", managerParts);

      handleManagerHardwarePartSubmit(managerParts, projectId, cleanedHardwareParts, payload)
    } else if (emp?.customRole[0] === "operations") {
      payload = {
        projectid: projectId,
        tasktype: "other",
        taskownerid: emp?._id,
        tasks: cleanedHardwareParts,
      };
      handleOperationHardwarePartSubmit(payload, projectId, cleanedHardwareParts)
    } else {
      return;
    }
  };

  const handleManagerHardwarePartSubmit = async (managerParts, projectId, cleanedHardwareParts, payload) => {
    setIsLoading(true);
    try {
      await dispatch(updateHardwareDescriptionData(managerParts));
      await dispatch(replaceHardwareProjectTrack(payload));
      await dispatch(setProjectReadTrue(projectId));
      enqueueSnackbar("Hardware Parts Saved", {
        variant: "success",
      });
      dispatch(fetchHardwareProjectTracking());
      dispatch(fetchAllProjects());
      refreshAssign();
      dispatch(triggerEmployeesAssignedRefresh());
      dispatch(triggerEmployeesAssignedStateRefresh("submitted"));
    } catch {
      enqueueSnackbar("Failed to save Hardware", {
        variant: "error",
      });
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 3000)
      refreshAssign();
      setHardwarePartsOp(cleanedHardwareParts);
    }

  };

  const handleOperationHardwarePartSubmit = async (payload, projectId, cleanedHardwareParts) => {
    setIsLoading(true);
    try {
      await dispatch(replaceHardwareProjectTrack(payload));
      await dispatch(setProjectReadTrue(projectId));
      enqueueSnackbar("Hardware Parts Saved", {
        variant: "success",
      });
      dispatch(fetchHardwareProjectTracking());
      dispatch(fetchAllProjects());
      refreshAssign();
      dispatch(triggerEmployeesAssignedRefresh());
      dispatch(triggerEmployeesAssignedStateRefresh("submitted"));
    } catch {
      enqueueSnackbar("Failed to save Hardware", {
        variant: "error",
      });
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 3000)
      refreshAssign();
      setHardwarePartsOp(cleanedHardwareParts);
    }
  };

  const handleSelfHardwareDescriptionEntriesSubmit = async (emp) => {
    if (isViewOnly) return;
    const descriptionList = projectDescriptionsAssiged;
    const projectId = emp?.projectid;
    // For manager (Self), use manager-only value per entry so operation person amount is not merged in
    const cleanedDescriptionEntries = descriptionList
      .filter(
        (entry) =>
          entry?.description !== null &&
          entry?.description !== undefined &&
          entry?.description !== "" &&
          entry?.value !== null &&
          entry?.value !== undefined &&
          entry?.value !== ""
      )
      .map((entry) => ({
        _id: entry?._id || null,
        description: entry.description,
        value: isSelfManager
          ? getManagerOnlyAmountForSave(entry)
          : parseAmountNumber(entry.value),
        notes: entry.notes,
        oldtaskid: entry.oldtaskid || null,
        taskownerid: entry.taskownerid || null,
      }));
    console.log(
      "handleSelfHardwareDescriptionEntriesSubmit cleanedDescriptionEntries",
      cleanedDescriptionEntries,
      descriptionList
    );
    let payload; // <-- define it here
    if (emp?.customRole[0] === "project manager") {
      // const managerDescriptions = cleanedDescriptionEntries?.filter((part) => part?.taskownerid === emp?._id)
      const managerDescriptions = cleanedDescriptionEntries.filter((part) => {
        // case 1: taskownerid is missing → assign to manager
        if (
          part?.taskownerid === null ||
          part?.taskownerid === undefined ||
          part?.taskownerid === ""
        ) {
          return true;
        }

        // case 2: taskownerid exists and matches manager
        return String(part.taskownerid) === String(emp?._id);
      });

      payload = {
        projectid: projectId,
        tasktype: "self",
        entries: managerDescriptions,
      };
      handleManagerHardwareDescriptionSubmit(managerDescriptions, cleanedDescriptionEntries, payload)
    } else if (emp?.customRole[0] === "operations") {
      payload = {
        projectid: projectId,
        tasktype: "other",
        taskownerid: emp?._id,
        entries: cleanedDescriptionEntries,
      };
      handleOperationHardwareDescriptionSubmit(payload, cleanedDescriptionEntries)
    } else {
      return;
    }
  };

  const handleManagerHardwareDescriptionSubmit = async (managerDescriptions, cleanedDescriptionEntries, payload) => {
    setIsLoading(true);
    try {
      await dispatch(updateDescriptionData(managerDescriptions));
      await dispatch(saveUpdateDescriptionTask(payload));
      enqueueSnackbar("Description Entries Saved", {
        variant: "success",
      });
      dispatch(fetchAllProjects());
      dispatch(fetchDescriptionTasksProjectTracking());
      dispatch(triggerEmployeesAssignedRefresh());
      dispatch(triggerEmployeesAssignedStateRefresh("submitted"));
    } catch {
      enqueueSnackbar("Failed to save Description", {
        variant: "error",
      });
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 3000)
      setProjectDescriptionsAssiged(cleanedDescriptionEntries);
    }
  };

  const handleOperationHardwareDescriptionSubmit = async (payload, cleanedDescriptionEntries) => {
    setIsLoading(true);
    try {
      await dispatch(saveUpdateDescriptionTask(payload));
      enqueueSnackbar("Description Entries Saved", {
        variant: "success",
      });
      dispatch(fetchAllProjects());
      dispatch(fetchDescriptionTasksProjectTracking());
      dispatch(triggerEmployeesAssignedRefresh());
      dispatch(triggerEmployeesAssignedStateRefresh("submitted"));
    } catch {
      enqueueSnackbar("Failed to save Description", {
        variant: "error",
      });
      setIsLoading(false);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 3000)
      setProjectDescriptionsAssiged(cleanedDescriptionEntries);
    }
  };

  // After submit, neither manager nor operation person can edit Other Expenses
  const isLockedAfterSubmit = emp?.Assignedperson?.projecttracktaskStatus === "submit";

  return (
    <>
      <div className="w-full h-full">
        {clickedPersonId === emp?._id ? (
          <>
            {showListState === "tasktable" && (
              <HardwareEditAddOmAssigned
                emp={emp}
                setHardwarePartsOp={setHardwarePartsOp}
                hardwarePartsOp={hardwarePartsOp}
                assignedGroup={row?.AssignedGroup}
                assignedGroupId={
                  row?.groupid?._id ||
                  row?.groupid?.id ||
                  row?.groupid ||
                  row?.groupId?._id ||
                  row?.groupId?.id ||
                  row?.groupId ||
                  row?.group?._id ||
                  row?.group?.id ||
                  row?.group ||
                  row?.lead?.groupid?._id ||
                  row?.lead?.groupid?.id ||
                  row?.lead?.groupid ||
                  row?.lead?.groupId?._id ||
                  row?.lead?.groupId?.id ||
                  row?.lead?.groupId
                }
                isViewOnly={isViewOnly}
              />
            )}

            {showListState === "otherexpenses" && (
              <div className="w-full overflow-auto justify-center pb-2">
                <div className="max-h-[14rem] ">
                  <div className="sticky top-0 flex text-center bg-blue-500 text-white text-[11px] z-20">
                    <div className="w-[45%] border p-1">Descriptions</div>
                    <div className="w-[20%] border p-1">
                      Amount ({currency})
                    </div>
                    <div className="w-[35%] border p-1">Notes</div>
                  </div>

                  <div className="sticky top-[24px] flex text-center bg-white text-black text-[11px] z-10">
                    <div className="w-[45%] border p-1">Total</div>
                    <div className="w-[20%] border p-1">
                      {formatAmountComma(
                        isSelfManager
                          ? projectDescriptionsAssiged?.reduce(
                              (sum, item) => sum + getDisplayAmount(item),
                              0
                            ) + (operationOnlyDisplayRows?.reduce((sum, row) => sum + parseAmountNumber(row.value), 0) ?? 0)
                            + (shouldShowLockedTrainingRow ? lockedTrainingCostTotal : 0)
                          : projectDescriptionsAssiged?.reduce((sum, item) => {
                              return sum + parseAmountNumber(item.value);
                            }, 0) + (shouldShowLockedTrainingRow ? lockedTrainingCostTotal : 0)
                      )}
                    </div>
                    <div className="w-[35%] border p-1"></div>
                  </div>
                  {(String(row?.TypeService ?? "").toLowerCase().includes("installation")) && (
                    <div className="sticky top-[24px] flex text-center bg-white text-black text-[11px] z-10">
                      <div className="w-[45%] border p-1 flex justify-center items-center">
                        Trip Duration
                      </div>
                      <div className="w-[20%] border p-1">
                        <input
                          value={tripDuration}
                          type="text"
                          name="tripduration"
                          placeholder="trip duration"
                          onChange={handleTripDuration}
                          className={`outline-none border-none text-center w-full h-full p-0.5 border border-white text-black rounded-md `}
                        />
                      </div>
                      <div className="w-[35%] border p-1"></div>
                    </div>
                  )}

                  <div className="w-full">
                    {projectDescriptionsAssiged?.map((row, index) => {
                      const matchedOldDescription =
                        matchedOldRowDesctiption.find(
                          (old) => old._id === row.oldtaskid
                        );
                      return (
                        <React.Fragment key={index}>
                          <div className="flex items-center text-center w-full text-[11px]">
                            <div className="w-full">
                              <div className="head flex">
                                <div
                                  className="w-[45%] border border-white"
                                  title={
                                    matchedOldDescription
                                      ? `Previous Name: ${matchedOldDescription.description}`
                                      : isSelfManager && isDescriptionCommon(row.description)
                                        ? "Sum row (display only); cannot edit"
                                        : ""
                                  }
                                >
                                  <input
                                    type="text"
                                    name="description"
                                    value={row.description}
                                    onChange={(e) =>
                                      handleTripInputChange(
                                        index,
                                        "description",
                                        validateDescriptionOrNotes(
                                          e.target.value
                                        )
                                      )
                                    }
                                    readOnly={
                                      isLockedAfterSubmit ||
                                      isCommonConsolidatedRow(row)
                                    }
                                    className={`outline-none border-none text-center w-full h-full p-1 ${row.lastEditedFields?.includes(
                                      "description"
                                    )
                                      ? "bg-gray-400 text-white border "
                                      : "border border-white bg-gray-200"
                                      } ${isLockedAfterSubmit || isCommonConsolidatedRow(row) ? "cursor-default bg-gray-100" : ""}`}
                                  />
                                </div>

                                <div
                                  className="w-[20%] border border-white"
                                  title={
                                    matchedOldDescription
                                      ? `Previous Value: ${matchedOldDescription.value}`
                                      : isCommonConsolidatedRow(row)
                                        ? "Sum of manager + operation persons (display only, not saved)"
                                        : ""
                                  }
                                >
                                  <input
                                    type="text"
                                    name="value"
                                    value={formatAmountForInput(
                                      isCommonConsolidatedRow(row)
                                        ? getDisplayAmount(row)
                                        : row.value
                                    )}
                                    onChange={(e) =>
                                      handleTripInputChange(
                                        index,
                                        "value",
                                        validateAmountValue(e.target.value)
                                      )
                                    }
                                    readOnly={
                                      isLockedAfterSubmit ||
                                      isCommonConsolidatedRow(row)
                                    }
                                    onKeyDown={(e) => {
                                      if (
                                        ["e", "E", "+", "-"].includes(e.key)
                                      ) {
                                        e.preventDefault();
                                      }
                                    }}
                                    className={`outline-none border-none text-center w-full h-full p-1 ${row.lastEditedFields?.includes("value")
                                      ? "bg-gray-400 text-white border "
                                      : "border border-white bg-gray-200"
                                      } ${isLockedAfterSubmit || isCommonConsolidatedRow(row) ? "cursor-default bg-gray-100" : ""}`}
                                  />
                                </div>

                                <div
                                  className="w-[35%] border border-white"
                                  title={
                                    matchedOldDescription
                                      ? `Previous Notes: ${matchedOldDescription.notes}`
                                      : isCommonConsolidatedRow(row)
                                        ? "Sum row (display only); cannot edit"
                                        : ""
                                  }
                                >
                                  <input
                                    type="text"
                                    name="notes"
                                    value={row.notes}
                                    onChange={(e) =>
                                      handleTripInputChange(
                                        index,
                                        "notes",
                                        validateDescriptionOrNotes(
                                          e.target.value
                                        )
                                      )
                                    }
                                    readOnly={
                                      isLockedAfterSubmit ||
                                      isCommonConsolidatedRow(row)
                                    }
                                    className={`outline-none border-none text-center w-full h-full p-1 ${row.lastEditedFields?.includes("notes")
                                      ? "bg-gray-400 text-white border "
                                      : "border border-white bg-gray-200"
                                      } ${isLockedAfterSubmit ? "cursor-default bg-gray-100" : ""}`}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                        </React.Fragment>
                      );
                    })}
                    {/* Self only: show operation-only rows (descriptions not matched with manager) — one row each with sum of amount; read-only, owner stays operation */}
                    {isSelfManager &&
                      operationOnlyDisplayRows?.map((row, opIndex) => (
                        <div
                          key={`op-only-${opIndex}`}
                          className="flex items-center text-center w-full text-[11px]"
                        >
                          <div className="w-full">
                            <div className="head flex">
                              <div className="w-[45%] border border-white" title="Operation entry (display only)">
                                <input
                                  type="text"
                                  readOnly
                                  value={row.description}
                                  className="outline-none border-none text-center w-full h-full p-1 border border-white bg-gray-100 cursor-default"
                                />
                              </div>
                              <div className="w-[20%] border border-white" title="Operation amount (display only)">
                                <input
                                  type="text"
                                  readOnly
                                  value={formatAmountComma(Number(row.value) || 0)}
                                  className="outline-none border-none text-center w-full h-full p-1 border border-white bg-gray-100 cursor-default"
                                />
                              </div>
                              <div className="w-[35%] border border-white">
                                <input
                                  type="text"
                                  readOnly
                                  value={row.notes}
                                  className="outline-none border-none text-center w-full h-full p-1 border border-white bg-gray-100 cursor-default"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                    {shouldShowLockedTrainingRow && lockedTrainingRow && (
                      <div className="flex items-center text-center w-full text-[11px]">
                        <div className="w-full">
                          <div className="head flex">
                            <div className="w-[45%] border border-white" title="Training cost (display only)">
                              <input
                                type="text"
                                readOnly
                                value={lockedTrainingRow.description}
                                className="outline-none border-none text-center w-full h-full p-1 border border-white bg-gray-100 text-[#6b7280] cursor-default"
                              />
                            </div>
                            <div className="w-[20%] border border-white" title="Training cost (display only)">
                              <input
                                type="text"
                                readOnly
                                value={formatAmountComma(lockedTrainingRow.amount)}
                                className="outline-none border-none text-center w-full h-full p-1 border border-white bg-gray-100 text-[#6b7280] cursor-default"
                              />
                            </div>
                            <div className="w-[35%] border border-white">
                              <input
                                type="text"
                                readOnly
                                value={lockedTrainingRow.notes}
                                className="outline-none border-none text-center w-full h-full p-1 border border-white bg-gray-100 text-[#6b7280] cursor-default"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Single control row after all rows, including the locked Training Cost row. */}
                    {!(
                        (emp.fullName === "Self" ||
                          emp.customRole?.[0] === "project manager" ||
                          emp.customRole?.[0] === "operations") &&
                        emp?.Assignedperson?.projecttracktaskStatus === "submit"
                      ) &&
                      emp.Assignedperson?.operationaction !== "reject" &&
                      (emp.Assignedperson?.projecttracktaskStatus === "pending" ||
                        (!(
                          emp.Assignedperson?.manageraction === "complete" ||
                          emp.Assignedperson?.operationaction === "complete"
                        ) &&
                          emp.Assignedperson?.projecttracktaskStatus === "submit")) && (
                        <div className="flex justify-between px-2 pt-1">
                          <button
                            className="shadow-xl rounded-md p-1"
                            onClick={handleAddRow}
                            disabled={projectDescriptionsAssiged?.length >= 15}
                          >
                            <AddIcon />
                          </button>
                          <button
                            className="shadow-xl rounded-md p-1"
                            onClick={() => {
                              const lastIndex = projectDescriptionsAssiged?.length - 1;
                              if (lastIndex >= 0) {
                                const lastRow = projectDescriptionsAssiged[lastIndex];
                                handleRemoveRow(lastIndex);
                                dispatch(deleteDescriptionTask(lastRow?._id));
                              }
                            }}
                            disabled={projectDescriptionsAssiged?.length <= 0}
                          >
                            <RemoveIcon />
                          </button>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}

            {emp.Assignedperson?.operationaction !== "reject" && (
              <>
                {(emp.Assignedperson?.projecttracktaskStatus === "pending" ||
                  (!(
                    emp.Assignedperson?.manageraction === "complete" ||
                    emp.Assignedperson?.operationaction === "complete"
                  ) &&
                    emp.Assignedperson?.projecttracktaskStatus ===
                    "submit")) && (
                    <>
                      {(showListState === "otherexpenses" ||
                        showListState === "tasktable") && (
                          <div className="flex justify-end mb-2 mr-2">
                            <>
                              <>
                                {emp?.customRole[0] === "project manager" ? (
                                  <>
                                    {!(
                                      (emp.fullName === "Self" ||
                                        emp.customRole[0] === "project manager" ||
                                        emp.customRole[0] === "operations") &&
                                      emp?.Assignedperson
                                        ?.projecttracktaskStatus === "submit"
                                    ) && (
                                        <button
                                          disabled={isViewOnly}
                                          onClick={() => {
                                            if (isViewOnly) return;
                                            handleHardwarePartSubmit(emp);
                                            handleSelfHardwareDescriptionEntriesSubmit(
                                              emp
                                            );
                                          }}
                                          className={`px-2 py-1 rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed opacity-60 pointer-events-none" : "bg-blue-600 hover:bg-blue-800 text-white"}`}
                                        >
                                          Submit
                                        </button>
                                      )}
                                  </>
                                ) : (
                                  emp?.customRole[0] === "operations" && (
                                    <>
                                      {!(
                                        (emp.fullName === "Self" ||
                                          emp.customRole[0] === "project manager" ||
                                          emp.customRole[0] === "operations") &&
                                        emp?.Assignedperson
                                          ?.projecttracktaskStatus === "submit"
                                      ) && (
                                          <button
                                            disabled={isViewOnly}
                                            onClick={() => {
                                              if (isViewOnly) return;
                                              handleHardwarePartSubmit(emp);
                                              handleSelfHardwareDescriptionEntriesSubmit(
                                                emp
                                              );
                                            }}
                                            className={`px-2 py-1 rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed opacity-60 pointer-events-none" : "bg-blue-600 hover:bg-blue-800 text-white"}`}
                                          >
                                            Submit
                                          </button>
                                        )}
                                    </>
                                  )
                                )}
                              </>
                            </>
                          </div>
                        )}
                    </>
                  )}
              </>
            )}
          </>
        ) : (
          <div
            onClick={() => {
              const isOpening = clickedPersonId !== emp?._id;
              if (!isViewOnly && isOpening) {
                syncToolGuideStep("view-details");
              }
              setClickedPersonId(clickedPersonId === emp?._id ? null : emp?._id);
            }}
            className="h-full flex-1 flex justify-center items-center align-middle"
          >
            Task Details
          </div>
        )}
      </div>
    </>
  );
};

export default ShowAssigedTableManagerHardware;
